import { CustexCustomerDetailsModel } from '../models';
import { custexHttp } from './http';
import { plainToClass } from 'class-transformer';
import { channelHelper, custexHelper, errorToBasketErrorModel } from 'helpers';
import { isApiError, BasketApiErrorModel } from 'models/Errors';
import { APPLICATION_NAME } from '../helpers/constants';

export class CustexRepository {
    public async getCustomerDetails(userId: string): Promise<CustexCustomerDetailsModel> {
        try {
            // parameter timestamp is included to avoid caching request (not required parameter)
            const response = await custexHttp.get<CustexCustomerDetailsModel>(`/api/customers/${userId}`, {
                params: {
                    timestamp: new Date().getTime()
                }
            });

            let result = plainToClass(CustexCustomerDetailsModel, response.data);
            this.setContactOptions(result, response.data as any);

            return result;
        } catch (error) {
            const errorSource = 'custex.repository:getCustomerDetails';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async createCustomer(
        username: string,
        password: string,
        firstName?: string,
        lastName?: string
    ): Promise<{ guid: string }> {
        try {
            const custexUser = custexHelper.createCustexUser(username, password, firstName, lastName);

            const response = await custexHttp.post('/api/customers', custexUser, {
                params: {
                    channel: channelHelper.getChannel(),
                    store: channelHelper.getChannel()
                }
            });

            return response.data;
        } catch (error) {
            const errorSource = 'custex.repository:createCustomer';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async updateCustomerDetails(customerEmail: string, customerId: string, customerDetails: CustexCustomerDetailsModel): Promise<void> {
        try {
            const requestModel: any = {
                title: customerDetails.title,
                firstname: customerDetails.firstName,
                surname: customerDetails.surname,
                addresses: customerDetails.addresses,
                phoneNumbers: customerDetails.phoneNumbers,
                modifiedByUser: `${customerEmail}:${APPLICATION_NAME}`
            };

            await custexHttp.put(`/api/customers/${customerId}`, requestModel, {
                params: {
                    channel: channelHelper.getChannel(),
                    store: channelHelper.getChannel()
                }
            });
        } catch (error) {
            const errorSource = 'custex.repository:updateCustomerDetails';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    public async updateCustomerDetailsContactOptions(customerId: string, type: 'email' | 'phone', isAllowed: boolean): Promise<void> {
        try {
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            const nonTransactionalPreferences = [1, 4, 5];
            const requestModel = nonTransactionalPreferences.map(typeId => ({
                contactTypeId: typeId,
                reason: 'Please tick the box if you would like to recieve information about our products or special offers',
                source: 'checkout',
                delete: false,
                [type === 'email' ? 'email' : 'phone']: isAllowed,
                [type === 'email' ? 'socialMedia' : 'sms']: isAllowed,
            }));

            await custexHttp.put(`/api/customers/${customerId}/contactoptions`, requestModel, {
                params: {
                    channel: channelHelper.getChannel(),
                    store: channelHelper.getChannel()
                }
            });
        } catch (error) {
            const errorSource = 'custex.repository:updateCustomerDetailsContactOptions';
            if (isApiError(error)) {
                throw new BasketApiErrorModel(error, errorSource);
            } else {
                throw errorToBasketErrorModel(error, errorSource);
            }
        }
    }

    private setContactOptions(model: CustexCustomerDetailsModel, data: { contactOptions: { contactTypeId: number; phone: boolean; email: boolean }[] }) {
        // copied from old system
        if (data.contactOptions && data.contactOptions.length > 0) {
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            let firstContactOption = data.contactOptions.find(c => c.contactTypeId !== 2 && c.contactTypeId !== 3);

            if (firstContactOption) {
                model.allowPhoneContacts = firstContactOption.phone;
                model.allowEmailContacts = firstContactOption.email;
            }
        }
    }
}

export const custex = new CustexRepository();
